import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Mail, CheckCircle2, Instagram, Facebook, Youtube } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        first_name: 'Newsletter Subscriber',
        source: 'newsletter'
      });
      setSubscribed(true);
      toast.success('Welcome to the Winamor family!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0a0518] relative overflow-hidden">
      {/* Newsletter Section */}
      <div className="border-b border-purple-500/20">
        <div className="container-custom py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Be the First to Know
            </h3>
            <p className="text-purple-300 mb-8">
              Subscribe for exclusive offers, keratin tips, and early access to new products.
            </p>
            
            {!subscribed ? (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  data-testid="footer-email-input"
                  className="input-glass flex-1"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-subscribe-btn"
                  className="btn-primary px-8"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Subscribe
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-2 text-green-400">
                <CheckCircle2 className="w-5 h-5" />
                <span>You're subscribed! Check your inbox.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/yx8h504c_logo%20transparent%20good%20%20%28Stickers%20%28Square%29%29%20%283%29.png" 
                alt="Winamor Australia - Professional Keratin Iron Brand Logo" 
                className="h-10 w-10"
                loading="lazy"
              />
              <h2 className="text-2xl font-bold text-white">WINAMOR</h2>
            </div>
            <p className="text-purple-300 text-sm mb-4">
              Australia's #1 keratin iron with pass counting technology. 
              Designed by professional hairdressers, for salons and home use.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/winamor_official/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center hover:bg-purple-500/40 transition-colors"
                data-testid="social-instagram"
                aria-label="Follow Winamor on Instagram"
              >
                <Instagram className="w-5 h-5 text-purple-300" />
              </a>
              <a 
                href="https://facebook.com/winamor" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center hover:bg-purple-500/40 transition-colors"
                data-testid="social-facebook"
                aria-label="Follow Winamor on Facebook"
              >
                <Facebook className="w-5 h-5 text-purple-300" />
              </a>
              <a 
                href="https://youtube.com/winamor" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center hover:bg-purple-500/40 transition-colors"
                data-testid="social-youtube"
                aria-label="Subscribe to Winamor on YouTube"
              >
                <Youtube className="w-5 h-5 text-purple-300" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Products</h4>
            <ul className="space-y-3">
              <li><a href="#products" className="nav-link text-sm">Perfect Pass Keratin Iron</a></li>
              <li><a href="#products" className="nav-link text-sm">Master Kit Bundle</a></li>
              <li><a href="#products" className="nav-link text-sm">Thermal Protectant</a></li>
              <li><a href="#products" className="nav-link text-sm">Accessories</a></li>
            </ul>
          </div>

          {/* Education */}
          <div>
            <h4 className="text-white font-semibold mb-4">Education</h4>
            <ul className="space-y-3">
              <li><a href="#class" className="nav-link text-sm">Keratin Masterclass</a></li>
              <li><a href="#" className="nav-link text-sm">How-To Guides</a></li>
              <li><a href="#" className="nav-link text-sm">Video Tutorials</a></li>
              <li><a href="#faq" className="nav-link text-sm">FAQ</a></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-semibold mb-4">Support</h4>
            <ul className="space-y-3">
              <li><a href="#contact" className="nav-link text-sm">Contact Us</a></li>
              <li><a href="https://docs.google.com/forms/d/17XKtX_YSsOKQ03uFmNbexqFINI8mErenUoWWMRZZitM/viewform" target="_blank" rel="noopener noreferrer" className="nav-link text-sm">Extended Warranty Form</a></li>
              <li><a href="https://docs.google.com/forms/d/1e03AlPCWF64vwpfuuQ4DhmV0IFcsuE4_8tHQLeGnw3k/viewform" target="_blank" rel="noopener noreferrer" className="nav-link text-sm">Returns Form</a></li>
            </ul>
            <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <p className="text-purple-300 text-xs">
                <strong className="text-white">Shipping:</strong> $10-$15 AUD<br/>
                Australia-wide delivery only<br/>
                Paid upon checkout
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-purple-500/20 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-purple-400 text-sm">
            © {currentYear} Winamor Australia. All rights reserved. | Best Keratin Iron Australia
          </p>
          <div className="flex gap-6">
            <a href="#" className="nav-link text-xs">Privacy Policy</a>
            <a href="#" className="nav-link text-xs">Terms of Service</a>
            <a href="#" className="nav-link text-xs">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
